import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { LanguageCode } from '~/shared/internationalization';

import type { RecommendedAgentsResponse } from '../../data-contract/recommended-agents';
import type { AgentLibraryFilters, AgentLibraryScope } from '../../fetchers/agents/recommended-agents-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';

const STALE_TIME_MS = 30_000;

export const recommendedAgentsKeys = {
    all: ['recommended-agents'] as const,
    attributions: (scope: AgentLibraryScope, agentId: string, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'attributions', scope.kind, scope.architectureId, agentId, language] as const,
    item: (architectureId: string, itemId: string, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'item', architectureId, itemId, language] as const,
    library: (scope: AgentLibraryScope, filters: AgentLibraryFilters, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'library', scope.kind, scope.architectureId, filters, language] as const,
    libraryFilters: (scope: AgentLibraryScope, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'library-filters', scope.kind, scope.architectureId, language] as const,
    libraryPositions: (architectureId: string, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'library-positions', architectureId, language] as const,
    position: (architectureId: string, positionId: string, language: LanguageCode) =>
        [...recommendedAgentsKeys.all, 'position', architectureId, positionId, language] as const,
};

export const useAgentAttributionsEnabled = (): boolean =>
    useApplicationContext().applicationState.featureFlagService.isFeatureFlagEnabled('agent-attributions');

/** Whether a profile shows its Automations tab: behind the flag, and never while the profile is edited. */
export const useAutomationsTabShown = (isEditing: boolean): boolean => useAgentAttributionsEnabled() && !isEditing;

export const useUiLocale = (): LanguageCode => useApplicationContext().applicationState.language.code;

export const useItemRecommendedAgents = ({
    architectureId,
    enabled = true,
    itemId,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
}): UseQueryResult<RecommendedAgentsResponse, Error> => {
    const { fetchItemRecommendedAgents } = useApplicationFetchers();
    const acceptLanguage = useUiLocale();

    return useQuery({
        enabled,
        queryFn: async ({ signal }) => {
            const result = await fetchItemRecommendedAgents({ acceptLanguage, architectureId, itemId, signal });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: recommendedAgentsKeys.item(architectureId, itemId, acceptLanguage),
        staleTime: STALE_TIME_MS,
        throwOnError: false,
    });
};

export const usePositionRecommendedAgents = ({
    architectureId,
    enabled = true,
    positionId,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly positionId: string;
}): UseQueryResult<RecommendedAgentsResponse, Error> => {
    const { fetchPositionRecommendedAgents } = useApplicationFetchers();
    const acceptLanguage = useUiLocale();

    return useQuery({
        enabled,
        queryFn: async ({ signal }) => {
            const result = await fetchPositionRecommendedAgents({ acceptLanguage, architectureId, positionId, signal });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: recommendedAgentsKeys.position(architectureId, positionId, acceptLanguage),
        staleTime: STALE_TIME_MS,
        throwOnError: false,
    });
};
